import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { searchAddressGateway } from '@/di-container/common/search-address/gateway/client';
import {
  GetSearchAddressRequest,
  SearchAddress as SearchAddressType,
} from '@remodeling/common';

function useSearchAddress() {
  const [page, setPage] = useState(1);
  const [value, setValue] = useState('');
  const [_value, _setValue] = useState(''); // 인풋값과 결과값의 구분용 상태값
  const [placeholder, setPlaceholder] = useState('검색어를 입력해 주세요');
  const [inputError, setInputError] = useState('');
  const [hasResult, setHasResult] = useState(false);
  const [addressList, setAddressList] = useState<SearchAddressType[] | null>(
    null,
  );

  const [totalNum, setTotalNum] = useState<number>(0);
  const INPUT_LIMIT_LENGTH = 30;
  const SIZE = 15;
  const { getSearchAddress } = searchAddressGateway();

  const handlePage = useCallback((_page: number) => {
    setPage(_page);
  }, []);

  const handleSearchInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  const handleSearchDelete = useCallback(() => {
    setValue('');
    setHasResult(false);
  }, []);

  const validationSearchInput = useCallback(() => {
    switch (true) {
      case value.length === 0:
        return {
          placeholder: '도로명주소, 건물명 또는 지번입력',
          error: '검색어를 입력해 주세요.',
        };
      case value.length === 1:
        return {
          placeholder: '도로명주소, 건물명 또는 지번입력',
          error: '검색어는 최소 2글자 이상 입력해 주세요.',
        };
      case value.length > INPUT_LIMIT_LENGTH:
        return {
          placeholder: '도로명주소, 건물명 또는 지번입력',
          error: '검색어가 너무 깁니다.',
        };
      default:
        return { placeholder: '검색어를 입력해 주세요', error: '' };
    }
  }, [value]);

  const getAddress = useCallback(
    async ({ currentPage }: { currentPage: number }) => {
      const req: GetSearchAddressRequest = {
        query: value,
        page: currentPage || 1,
        size: SIZE,
      };
      const res = await getSearchAddress.exec(req);
      if (res?.data) {
        setAddressList(res.data.content);
        setTotalNum(res.data.totalElements);
      } else {
        setAddressList(null);
        setTotalNum(0);
      }
    },
    [getSearchAddress, value],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setInputError(validationSearchInput().error);
      setPlaceholder(validationSearchInput().placeholder);

      setPage(1);
      validationSearchInput().error || (await getAddress({ currentPage: 1 }));
      _setValue(value);
      setHasResult(!validationSearchInput().error);
    },
    [validationSearchInput, getAddress, value],
  );

  useEffect(() => {
    validationSearchInput().error || getAddress({ currentPage: page || 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return {
    page,
    handlePage,
    placeholder,
    handleSearchInput,
    value,
    handleSearchDelete,
    handleSubmit,
    inputError,
    hasResult,
    _value,
    totalNum,
    addressList,
    SIZE,
  };
}

export default useSearchAddress;
